import { get, isEmpty, omit } from "lodash-es";
import { ActionType } from "../../constants/redux";
import {
  UPDATE_ERROR_MESSAGE,
  TOGGLE_ERROR_DIALOG,
  HomeState,
  GET_NEWS,
  CLEAR_NEWS,
  SET_CACHED_NEWS,
  SET_NEWS_ERROR,
  SET_NEWS_WITH_METADATA,
  APPEND_NEWS,
  CLEAR_NEWS_CACHE,
  VALIDATE_NEWS_CACHE,
  Story,
  NEWS_CACHE_CONFIG,
  FALLBACK_STORIES,
  isStoryArray,
} from "./type";
import { ActionStatus } from "../../constants/api";

const InitialState: HomeState = {
  currentTab: "0",
  news: [],
  NEWS_STATUS: ActionStatus.NONE,
  error: null,
  lastFetched: null,
  cached: false,
  loading: false,
};

const HomeReducer = (state = InitialState, action: ActionType): HomeState => {
  switch (action.type) {
    case UPDATE_ERROR_MESSAGE.name: {
      return {
        ...state,
        error: action.payload || null,
      };
    }

    case TOGGLE_ERROR_DIALOG.name: {
      return {
        ...state,
        // Add any dialog state management if needed
      };
    }

    // GET_NEWS actions
    case GET_NEWS.name: {
      return {
        ...state,
        NEWS_STATUS: ActionStatus.LOADING,
        error: null,
        loading: true,
      };
    }

    case GET_NEWS.success: {
      const rawData = get(action.payload, "response", action.payload);
      let processedNews: Story[] = [];

      try {
        if (rawData && rawData.data && rawData.data.children) {
          processedNews = rawData.data.children
            .slice(0, NEWS_CACHE_CONFIG.MAX_STORIES)
            .map((post: any) => {
              const imageUrl =
                post.data.thumbnail && post.data.thumbnail.startsWith("http")
                  ? post.data.thumbnail
                  : post.data.preview?.images[0]?.source?.url ||
                    "https://placehold.co/600x400/png";

              return {
                title: post.data.title,
                link: `https://reddit.com${post.data.permalink}`,
                pubDate: new Date(post.data.created_utc * 1000).toISOString(),
                author: `u/${post.data.author}`,
                image: imageUrl.replace(/&amp;/g, "&"),
              };
            });
        } else if (isStoryArray(rawData)) {
          processedNews = rawData;
        } else if (Array.isArray(rawData)) {
          processedNews = rawData;
        } else {
          console.warn("Unexpected news data format:", rawData);
          processedNews = [];
        }

        if (processedNews.length > 0) {
          localStorage.setItem(
            NEWS_CACHE_CONFIG.STORAGE_KEY,
            JSON.stringify(processedNews)
          );
          localStorage.setItem(
            NEWS_CACHE_CONFIG.TIMESTAMP_KEY,
            Date.now().toString()
          );
        }

        return {
          ...state,
          NEWS_STATUS: ActionStatus.SUCCESS,
          news: processedNews,
          error: null,
          loading: false,
          lastFetched: Date.now(),
          cached: false,
        };
      } catch (error) {
        console.error("Error processing news data:", error);
        return {
          ...state,
          NEWS_STATUS: ActionStatus.FAILURE,
          error: "Failed to process news data",
          loading: false,
        };
      }
    }

    case GET_NEWS.failure: {
      const status = get(action.payload, "status", 0);
      const p = get(action.payload, "response", {});
      const errorMessage = get(action.payload, "message", "Unknown error");

      const response =
        status === 500 && isEmpty(p)
          ? "Server error. Please try again later."
          : p?.display_message || p?.message || errorMessage;

      // Try to load cached news on failure
      let cachedNews: Story[] = [];
      let useFallback = false;

      try {
        const cachedStories = localStorage.getItem(
          NEWS_CACHE_CONFIG.STORAGE_KEY
        );
        if (cachedStories) {
          cachedNews = JSON.parse(cachedStories);
          if (!isStoryArray(cachedNews)) {
            cachedNews = [];
            useFallback = true;
          }
        } else {
          useFallback = true;
        }
      } catch (error) {
        console.error("Error loading cached news:", error);
        useFallback = true;
      }

      return {
        ...state,
        NEWS_STATUS: ActionStatus.FAILURE,
        error:
          typeof response === "string" ? response : JSON.stringify(response),
        loading: false,
        news: useFallback ? FALLBACK_STORIES : cachedNews,
        cached: !useFallback,
      };
    }

    // New action handlers
    case CLEAR_NEWS.name: {
      return {
        ...state,
        news: [],
        error: null,
        NEWS_STATUS: ActionStatus.NONE,
        lastFetched: null,
        cached: false,
        loading: false,
      };
    }

    case SET_CACHED_NEWS.name: {
      const cachedStories = action.payload || [];
      return {
        ...state,
        news: isStoryArray(cachedStories) ? cachedStories : [],
        cached: true,
        error: null,
        NEWS_STATUS: ActionStatus.SUCCESS,
        loading: false,
      };
    }

    case SET_NEWS_ERROR.name: {
      return {
        ...state,
        error: action.payload || "Unknown error",
        NEWS_STATUS: ActionStatus.FAILURE,
        loading: false,
      };
    }

    case SET_NEWS_WITH_METADATA.name: {
      const { stories, cached, timestamp } = action.payload || {};
      return {
        ...state,
        news: isStoryArray(stories) ? stories : [],
        cached: Boolean(cached),
        lastFetched: timestamp || Date.now(),
        error: null,
        NEWS_STATUS: ActionStatus.SUCCESS,
        loading: false,
      };
    }

    case APPEND_NEWS.name: {
      const newStories = action.payload || [];
      if (isStoryArray(newStories)) {
        return {
          ...state,
          news: [...state.news, ...newStories],
          error: null,
        };
      }
      return state;
    }

    case CLEAR_NEWS_CACHE.name: {
      try {
        localStorage.removeItem(NEWS_CACHE_CONFIG.STORAGE_KEY);
        localStorage.removeItem(NEWS_CACHE_CONFIG.TIMESTAMP_KEY);
      } catch (error) {
        console.error("Error clearing news cache:", error);
      }
      return {
        ...state,
        cached: false,
        lastFetched: null,
      };
    }

    case VALIDATE_NEWS_CACHE.name: {
      try {
        const cacheTime = localStorage.getItem(NEWS_CACHE_CONFIG.TIMESTAMP_KEY);
        const isCacheValid =
          cacheTime &&
          Date.now() - parseInt(cacheTime, 10) <
            NEWS_CACHE_CONFIG.CACHE_DURATION;

        if (!isCacheValid) {
          localStorage.removeItem(NEWS_CACHE_CONFIG.STORAGE_KEY);
          localStorage.removeItem(NEWS_CACHE_CONFIG.TIMESTAMP_KEY);
          return {
            ...state,
            cached: false,
            lastFetched: null,
          };
        }
      } catch (error) {
        console.error("Error validating cache:", error);
      }
      return state;
    }

    default:
      return state;
  }
};

export default HomeReducer;
